/*
 * COPYRIGHT (c) 2023
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, DatePicker, Icon, Input } from '@scuf/common';
import React, { useEffect, useState } from 'react';
import { IAny } from '../DashboardKPIWidgets/IEnergyManagementProps';
import { getSymbolFromCurrency } from '../shared/Utils/Utils';
import {
  formatDateFor,
  formatDateforSave
} from '../DetailKPIWidgets/UtilityBillViewDetailsComponent/UtilityBillViewDetailsUtils';
import moment from 'moment';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../locales/en-US/descriptors';

export const UtilityBillFormComponent = (props: IAny) => {
  const {
    billDetails,
    setBillDetails,
    isNewBill,
    utilityCellData,
    totalCostError,
    setTotalCostError,
    totalConsumptionError,
    setTotalConsumptionError,
    totalDemandError,
    setTotalDemandError,
    toggleAutomaticBillCategories,
    isPDFExtracted,
    ocrManageBill
  } = props;
  const [t, lng] = useTranslation();
  const ub = descriptors.UtilityBuilder;
  const d = descriptors.energyManagement;
  // const [rangeValue, setRangeValue] = useState<IAny>({ to: formatDateFor(billDetails.startDate), from: formatDateFor(billDetails.endDate) });
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [rangeValue, setRangeValue] = useState<IAny>({
    to: '',
    from: ''
  });
  const [closeOnSelection, setCloseOnSelection] = useState(false);
  const [closeOnDocumentClick, setCloseOnDocumentClick] = useState(true);
  const [tFormConsp, setTFormConsp] = useState<IAny>('');
  const [tFormCost, setTFormCost] = useState<IAny>('');
  const [tFormDemand, setTFormDemand] = useState<IAny>('');
  const [errors, setErrors] = useState<IAny>('');

  const todayDate = moment().format('YYYY-MM-DD');
  let lastYearDate = moment()
    .subtract(4, 'years')
    .format('YYYY-MM-DD');

  useEffect(() => {
    if (props.entryFromPDF) {
      setTFormConsp(billDetails.totalConsumption);
      setTFormCost(billDetails.totalCost);
      setTFormDemand(billDetails.totalDemand);
      props.setEntryFromPDF(false);
    }
  }, [props.entryFromPDF]);

  useEffect(() => {
    if (!isNewBill) {
      let temRange = { ...rangeValue };
      temRange.to = moment(billDetails.endDate, 'YYYY-MM-DD');
      temRange.from = moment(billDetails.startDate, 'YYYY-MM-DD');
      setRangeValue(temRange);
    }
  }, [billDetails]);

  useEffect(() => {
    setTFormConsp(billDetails.totalConsumption);
    setTFormCost(billDetails.totalCost);
    setTFormDemand(billDetails.totalDemand);
  }, []);

  useEffect(() => {
    if (isNewBill) {
      let temRange = { to: '', from: '' };
      setRangeValue(temRange);
      setTFormConsp('');
      setTFormCost('');
      setTFormDemand('');
    }
  }, [isNewBill]);

  const autofillBillCategories = (
    value: string,
    name: string,
    total: number
  ) => {
    const tempBillDetails = JSON.parse(JSON.stringify(billDetails));
    tempBillDetails[name] = Number(value).toFixed(2);
    tempBillDetails.billCategories.otherCosts[
      tempBillDetails.billCategories.otherCosts.findIndex(
        (otherCost: IAny) => otherCost.categoryName === 'Others'
      )
    ].cost = (Number(value) - total).toFixed(2);
    setBillDetails(tempBillDetails);
  };

  const autofillConsumption = (value: string, name: string) => {
    const tempBillDetails = JSON.parse(JSON.stringify(billDetails));
    tempBillDetails[name] = Number(value).toFixed(2);
    if (name === 'totalConsumption') {
      tempBillDetails.billCategories.consumption.forEach(
        (consumption: IAny) => {
          consumption.consumption = (
            Number(value) / tempBillDetails.billCategories.consumption.length
          ).toFixed(2);
        }
      );
      setBillDetails(tempBillDetails);
    } else {
      tempBillDetails.billCategories.demand.forEach((demand: IAny) => {
        demand.demand = (
          Number(value) / tempBillDetails.billCategories.demand.length
        ).toFixed(2);
      });
      setBillDetails(tempBillDetails);
    }
  };

  const calculateTotalCost = (billDetails: IAny) => {
    let totalCost = 0;
    if (billDetails && billDetails.billCategories) {
      billDetails.billCategories.otherCosts.forEach((otherCost: IAny) => {
        if (otherCost.categoryName !== 'Others') {
          totalCost += Number(otherCost.cost);
        }
      });
      billDetails.billCategories.consumption &&
        billDetails.billCategories.consumption.forEach((consumption: IAny) => {
          totalCost += Number(consumption.cost);
        });
      billDetails.billCategories.demand &&
        billDetails.billCategories.demand.forEach((demand: IAny) => {
          totalCost += Number(demand.cost);
        });
    }
    return totalCost;
  };

  const debounce = (fn: IAny, delay: IAny) => {
    let timer: IAny;
    return (...args: IAny) => {
      clearTimeout(timer);
      setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const handleBillDetail = (value: string, name: string) => {
    if (
      toggleAutomaticBillCategories &&
      (name === 'totalConsumption' || name === 'totalDemand')
    ) {
      // autofillConsumption(value, name);
    } else if (toggleAutomaticBillCategories && name === 'totalCost') {
      // autofillBillCategories(value, name, calculateTotalCost(billDetails));
    } else if (!isNaN(Number(value))) {
      setBillDetails({ ...billDetails, [name]: Number(value).toFixed(2) });
    } else {
      setBillDetails({ ...billDetails, [name]: value });
    }
    props.setIsUpdateTable(true);
  };
  const handleBillingCycle = (to: IAny, from: IAny) => {
    let fromDate = moment(from).format('YYYY-MM-DD');
    let toDate = moment(to).format('YYYY-MM-DD');
    if (fromDate < lastYearDate || toDate > todayDate) {
      setErrors('Select valid billing cycle date.');
    } else if (to === undefined) {
      setErrors('Select valid billing cycle date.');
    } else if (to === undefined && from === undefined) {
      setErrors('');
    } else if (typeof to === 'object' && typeof from === 'object') {
      setErrors('');
    }

    setBillDetails({
      ...billDetails,
      startDate:
        typeof from === 'object'
          ? formatDateforSave(
              typeof from === 'object' ? from.toDateString() : ''
            )
          : '',
      endDate:
        typeof to === 'object'
          ? formatDateforSave(typeof to === 'object' ? to.toDateString() : '')
          : '',
      billName:
        typeof to === 'object'
          ? moment(
              formatDateforSave(
                typeof to === 'object' ? to.toDateString() : ''
              ),
              'YYYY-MM-DD'
            ).format('MMM, YYYY')
          : '',
      billingDate:
        typeof to === 'object'
          ? moment(
              formatDateforSave(
                typeof to === 'object' ? to.toDateString() : ''
              ),
              'YYYY-MM-DD'
            ).format('DD MMM, YYYY')
          : ''
    });
    setTo(typeof to === 'object' ? to.toDateString() : '');
    setFrom(typeof from === 'object' ? from.toDateString() : '');
    setRangeValue({ to: to, from: from });
  };

  const handleChangeWithDebounce = (value: string, name: string) => {
    debounce(handleBillDetail, 1000)(value, name);
  };

  const [consumptionUnit, setConsumptionUnit] = useState();

  const [demandUnit, setDemandUnit] = useState();

  const [currencyUnit, setCurrencyUnit] = useState();

  const disableInputField = () => {
    if (!consumptionUnit || !currencyUnit) {
      return true;
    }
  };

  const disableDemandField = () => {
    if (!demandUnit) {
      return true;
    }
  };

  useEffect(() => {
    if (utilityCellData.integrationType === 'Automatic') {
      setConsumptionUnit(billDetails && billDetails.consumptionUnit);
      setDemandUnit(billDetails && billDetails.demandUnit);
      setCurrencyUnit(
        getSymbolFromCurrency(billDetails && billDetails.currencyUnit)
      );
    } else {
      setConsumptionUnit(utilityCellData && utilityCellData.consumptionUnit);
      setDemandUnit(utilityCellData && utilityCellData.demandUnit);
      setCurrencyUnit(
        utilityCellData && getSymbolFromCurrency(utilityCellData.currencyUnit)
      );
    }
  }, [isNewBill]);

  // useEffect(()=> {
  //   if(utilityCellData.integrationType === 'Automatic') {
  //     setConsumptionUnit(billDetails && billDetails.consumptionUnit);
  //     setDemandUnit(billDetails && billDetails.demandUnit);
  //     setCurrencyUnit(
  //       getSymbolFromCurrency(billDetails && billDetails.currencyUnit)
  //     );
  //   }
  // },[])

  let isDemandDisable =
    utilityCellData.demandUnit === '' && billDetails.demandUnit == null;
  return (
    <React.Fragment>
      <div className="billing-fields">
        <Input
          className="bill-name"
          placeholder=""
          value={billDetails.billName}
          label={`${t(ub.BILLNAME)}*`}
          disabled
          onChange={(value) => handleBillDetail(value, 'billName')}
        />
        <DatePicker
          className="bill-cycle"
          required={true}
          type="daterange"
          label={`${t(ub.BILLINGCYCLE)}*`}
          placeholder={t(ub.SELECT_PLACEHOLDER)}
          displayFormat="DD MMM YYYY"
          disabled={isNewBill || isPDFExtracted ? false : true}
          enabledBetween={{
            from: lastYearDate.toString(),
            to: todayDate.toString()
          }}
          showYearSelector={true}
          disablePast={false}
          error={errors}
          rangeValue={rangeValue}
          closeOnSelection={closeOnSelection}
          closeOnDocumentClick={closeOnDocumentClick}
          onRangeSelect={({ to, from }) => {
            props.setHasFieldChanged(true);
            handleBillingCycle(to, from);
          }}
          onTextChange={(value, error) => {
            setErrors('Select valid billing cycle date.');
          }}
        />
        {/* <Input
          className="bill-cycle"
          type=""
          placeholder="Select"
          value={`${formatDateFor(billDetails.startDate)} to ${formatDateFor(billDetails.endDate)}`}
          label="Billing Cycle*"
          onChange={(value) => handleBillDetail(value, 'billingCycle')}
        /> */}
        <Input
          className="bill-date"
          type=""
          placeholder=""
          value={
            billDetails &&
            billDetails.endDate &&
            moment(billDetails.endDate, 'YYYY-MM-DD')
              .locale(lng)
              .format('DD MMM, YYYY')
          }
          label={`${t(ub.BILLINGDATE)}*`}
          disabled
          onChange={(value) => handleBillDetail(value, 'billingDate')}
        />
      </div>
      <div className="cost-fields">
        <div className="Error-Wrapper-Builder">
          {/* <div className="Field-With-Autocompute"> */}
          <Input
            required={true}
            className="total-cost"
            type="number"
            placeholder={t(ub.ENTER_PLACEHOLDER)}
            disabled={
              (utilityCellData &&
                utilityCellData.integrationType === 'Automatic' &&
                !props.isNewBill) ||
              disableInputField()
            }
            value={tFormCost}
            label={
              currencyUnit && currencyUnit !== null
                ? `${t(ub.TOTAL_BILL_AMOUNT)} (${currencyUnit})*`
                : `${t(ub.TOTAL_BILL_AMOUNT)}`
            }
            onBlur={() => {
              if (!ocrManageBill) {
                setTimeout(() => {
                  autofillBillCategories(
                    tFormCost,
                    'totalCost',
                    calculateTotalCost(billDetails)
                  );
                  props.setIsUpdateTable(false);
                  props.setIsUpdateTable(true);
                  // setTimeout(()=> {setIsLoader(true)},1000);
                }, 100);
              }
            }}
            onKeyDown={(e: any) => {
              if (!ocrManageBill) {
                if (e.keyCode === 69 || e.keyCode === 189) {
                  e.preventDefault();
                } else {
                  return true;
                }
              }
            }}
            onChange={(value: IAny) => {
              props.setHasFieldChanged(true);
              setTotalCostError(false);
              setTFormCost(value);
              handleBillDetail(value, 'totalCost');
            }}
          />
          {/* <Button
            tabindex='0'
            onClick={() => {
              autofillBillCategories(tFormCost, 'totalCost', calculateTotalCost(billDetails))
              props.setIsUpdateTable(true);
            }}
            className="AutoCompute-Button"><Icon size='small' name='double-caret-right'/></Button> */}
          {/* </div> */}

          {totalCostError && (
            <span className="total-error">
              {props.isTariffPresent
                ? 'Total Bill Amount is not matching with Bill Categories and/or Tariff Details'
                : 'Total Bill Amount is not matching with Bill Categories'}
            </span>
          )}
        </div>
        <div className="Error-Wrapper-Builder">
          {/* <div className="Field-With-Autocompute"> */}
          <Input
            required={false}
            className="consumption-cost"
            type="number"
            placeholder={t(ub.ENTER_PLACEHOLDER)}
            disabled={
              (utilityCellData &&
                utilityCellData.integrationType === 'Automatic' &&
                !props.isNewBill) ||
              disableInputField()
            }
            value={tFormConsp}
            label={
              consumptionUnit && consumptionUnit !== null
                ? `${t(ub.BILLING_PERIOD_CONSUMPTION)} (${consumptionUnit})*`
                : `${t(ub.BILLING_PERIOD_CONSUMPTION)}`
            }
            onBlur={() => {
              if (!ocrManageBill) {
                setTimeout(() => {
                  autofillConsumption(tFormConsp, 'totalConsumption');
                  props.setIsUpdateTable(false);
                  props.setIsUpdateTable(true);
                }, 100);
              }
            }}
            onKeyDown={(e: any) => {
              if (!ocrManageBill) {
                if (e.keyCode === 69 || e.keyCode === 189) {
                  e.preventDefault();
                } else {
                  return true;
                }
              }
            }}
            onChange={(value: IAny) => {
              props.setHasFieldChanged(true);
              setTotalConsumptionError(false);
              setTFormConsp(value);
              handleBillDetail(value, 'totalConsumption');
            }}
          />
          {/* <Button 
            tabindex='1'
            onClick={() => {
              autofillConsumption(tFormConsp, 'totalConsumption')
              props.setIsUpdateTable(true);
            }}
            className="AutoCompute-Button"><Icon size='small' name='double-caret-right'/></Button> */}
          {/* </div> */}

          {totalConsumptionError && (
            <span className="total-error">
              {props.isTariffPresent
                ? 'Total consumption is not matching with Bill Categories and/or Tariff Details'
                : 'Total consumption is not matching with Bill Categories'}
            </span>
          )}
        </div>
        {
          <div className="Error-Wrapper-Builder">
            {/* <div className="Field-With-Autocompute"> */}
            <Input
              // required={true}
              className="total-demand"
              style={
                props.utilityCellData.utilityType === 'Electricity'
                  ? {}
                  : { visibility: 'hidden' }
              }
              type="number"
              placeholder={t(ub.ENTER_PLACEHOLDER)}
              disabled={
                (utilityCellData &&
                  utilityCellData.integrationType === 'Automatic' &&
                  !props.isNewBill) ||
                isDemandDisable ||
                disableDemandField()
              }
              value={tFormDemand}
              label={
                demandUnit && demandUnit !== null
                  ? `${t(ub.BILLING_DEMAND)} (${demandUnit})`
                  : t(ub.BILLING_DEMAND)
              }
              onBlur={() => {
                if (!ocrManageBill) {
                  setTimeout(() => {
                    autofillConsumption(tFormDemand, 'totalDemand');
                    props.setIsUpdateTable(false);
                    props.setIsUpdateTable(true);
                  }, 100);
                }
              }}
              onKeyDown={(e: any) => {
                if (!ocrManageBill) {
                  if (e.keyCode === 69 || e.keyCode === 189) {
                    e.preventDefault();
                  } else {
                    return true;
                  }
                }
              }}
              onChange={(value: IAny) => {
                props.setHasFieldChanged(true);
                setTotalDemandError(false);
                setTFormDemand(value);
                handleBillDetail(value, 'totalDemand');
              }}
            />
            {/* <Button 
              tabindex='2'
              onClick={() => {
                autofillConsumption(tFormDemand, 'totalDemand')
                props.setIsUpdateTable(true);
              }}
              className="AutoCompute-Button"><Icon size='small' name='double-caret-right'/></Button> */}
            {/* </div> */}

            {totalDemandError && (
              <span className="total-error">
                {props.isTariffPresent
                  ? 'Total Demand is not matching with Bill Categories and/or Tariff Details'
                  : 'Total Demand is not matching with Bill Categories'}
              </span>
            )}
          </div>
        }
      </div>
    </React.Fragment>
  );
};
